import React from "react";
import { useTranslation } from "react-i18next";
import { Plane } from "lucide-react";
import { AIRPORTS } from "@/features/return-home/data/airports";
import { useFormatDate } from "@/shared/hooks/useFormatDate";

interface TripSummaryCardProps {
  origin: string;
  destination: string;
  flightDate: string;
  returnDate: string;
  oneWay: boolean;
}

const RouteEnd: React.FC<{ code: string; align: "left" | "right" }> = ({ code, align }) => {
  const airport = AIRPORTS.find((a) => a.code === code);
  return (
    <div className={`min-w-0 flex-1 ${align === "right" ? "text-right" : ""}`}>
      <p className={`text-2xl font-bold tracking-wide ${code ? "text-gray-900" : "text-gray-300"}`}>
        {code || "—"}
      </p>
      {airport && (
        <>
          <p className="text-sm font-medium text-gray-700 truncate">{airport.city}</p>
          <p className="text-xs text-gray-500 truncate">{airport.name}</p>
        </>
      )}
    </div>
  );
};

/** Read-only route + dates summary shown at the top of the Return Home Info tab. */
export const TripSummaryCard: React.FC<TripSummaryCardProps> = ({
  origin,
  destination,
  flightDate,
  returnDate,
  oneWay,
}) => {
  const { t } = useTranslation();
  const { fmtDate } = useFormatDate();

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <RouteEnd code={origin} align="left" />
        <div className="flex items-center gap-1 text-indigo-400 shrink-0">
          <span className="w-6 border-t border-dashed border-indigo-300" />
          <Plane className="w-5 h-5 rotate-45" />
          <span className="w-6 border-t border-dashed border-indigo-300" />
        </div>
        <RouteEnd code={destination} align="right" />
      </div>
      <div className={`grid ${oneWay ? "grid-cols-1" : "grid-cols-2"} border-t border-dashed border-indigo-200 text-xs`}>
        <div className="px-4 py-2.5">
          <p className="text-gray-500 font-medium mb-0.5">{t("return_home.form.flight_date")}</p>
          <p className="text-sm font-semibold text-gray-900">{flightDate ? fmtDate(flightDate) : "—"}</p>
        </div>
        {!oneWay && (
          <div className="px-4 py-2.5 border-l border-dashed border-indigo-200 text-right">
            <p className="text-gray-500 font-medium mb-0.5">{t("return_home.form.return_date")}</p>
            <p className="text-sm font-semibold text-gray-900">{returnDate ? fmtDate(returnDate) : "—"}</p>
          </div>
        )}
      </div>
    </div>
  );
};
